import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useMemo, useState } from 'react';
import Spinner from '../../../shared/spinner';
import { format, parse, parseISO } from 'date-fns';
import { OperacaoParcela } from '../../../shared/model/operacaoParcela';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faClose, faCalculator, faSearch } from '@fortawesome/free-solid-svg-icons';
import InputMask from 'react-input-mask';
import DateInput from '../../../shared/dateInput';
import { BaixaParcelaDTO } from '../../../shared/dto/baixaParcelaDTO';
import { MovimentoFinanceiroEnum } from '../../../shared/constantes/movimentoFinanceiroEnum';
import { HistoricoFinanceiro } from '../../../shared/model/historicoFinanceiro';
import OperacaoParcelaService from '../../../services/operacaoParcelaService';
import HistoricoFinanceiroService from '../../../services/historicoFinanceiroService';
import CustomAlert from '../../../shared/customAlert';
import { calcularTotaisParcelasQuitacao, getParcelaQuitacaoKey } from '../../../shared/parcelasQuitacao';
import { BaixaParcelaFinanceiraPorEmpregadorDTO } from '../../../shared/dto/baixaParcelaFinanceiraPorEmpregadorDTO';
import { Empregador } from '../../../shared/model/empregador';
import EmpregadorService from '../../../services/empregadorService';
import { OperacaoParcelaFinanceiraEmpregador } from '../../../shared/model/operacaoParcelaFinanceiraEmpregador';

const formatarValorMoeda = (valor: number | null | undefined): string => {
  return (valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function BaixaParcelaFinanceiraPorEmpregadorComponent() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectFormaPagamento, setSelectFormaPagamento] = useState('SALDO_COM_DESCONTO');
  const [naturezaBaixa, setNaturezaBaixa] = useState('0');
  const [baixaDto, setBaixaDto] = useState<BaixaParcelaFinanceiraPorEmpregadorDTO>();
  const [parcelasList, setParcelasList] = useState<OperacaoParcelaFinanceiraEmpregador[] | null>([]);
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState<string[]>([]);
  const [exibeDetalhe, setExibeDetalhe] = useState(false);

  const [historicoFinanceiro, setHistoricoFinanceiro] = useState('');
  const [historicoFinanceiroList, setHistoricoFinanceiroList] = useState<HistoricoFinanceiro[]>([]);

  const [contrato, setContrato] = useState('');
  const [dtMovimento, setDtMovimento] = useState(format(parseISO(new Date().toISOString()), 'MM/yyyy'));
  const [nomeCliente, setNomeCliente] = useState('');
  const [faixaParcela1, setFaixaParcela1] = useState('');
  const [faixaParcela2, setFaixaParcela2] = useState('');
  const [valorPago, setValorPago] = useState<number>(0);
  const [valorPagoInput, setValorPagoInput] = useState<number>(0);
  const [confirmarBaixaValorInferior, setConfirmarBaixaValorInferior] = useState(false);

  const movimento = MovimentoFinanceiroEnum;

  const operacaoParcelaService: OperacaoParcelaService = new OperacaoParcelaService();
  const historicoFinanceiroService: HistoricoFinanceiroService = new HistoricoFinanceiroService();

  const [availableEmpregadores, setAvailableEmpregadores] = useState<Empregador[]>([]);
  const [selectedEmpregadores, setSelectedEmpregadores] = useState<Empregador[]>([]);
  const [selectedToMoveEmpregadores, setSelectedToMoveEmpregadores] = useState<Empregador[]>([]);
  const [selectedFromSelectedEmpregadores, setSelectedFromSelectedEmpregadores] = useState<Empregador[]>([]);
  const [empregador, setEmpregador] = useState('');
  const [nomeEmpregador, setNomeEmpregador] = useState('');

  const empregadorService: EmpregadorService = new EmpregadorService();

  // Helper para verificar se a parcela deve estar bloqueada para seleção
  const isParcelaBloqueada = (item: OperacaoParcelaFinanceiraEmpregador) => {
    return Boolean(item.isFinanceira && item.dtMovimentacaoFinanceira != null);
  };

  const parcelasSelecionadasList = (parcelasList ?? []).filter((item) =>
    parcelasSelecionadas.includes(getParcelaQuitacaoKey(item))
  );

  // Recalculado automaticamente em qualquer alteração no estado `parcelasSelecionadas`
  const totaisParcelasSelecionadas = calcularTotaisParcelasQuitacao(parcelasSelecionadasList);

  const parcelasHabilitadas = (parcelasList ?? []).filter((item) => !isParcelaBloqueada(item));
  const todasParcelasSelecionadas = parcelasHabilitadas.length > 0 &&
    parcelasHabilitadas.every((item) => parcelasSelecionadas.includes(getParcelaQuitacaoKey(item)));


  const listarEmpregadores = async () => {
    setLoading(true);
    try {
      const response = await empregadorService.listarTodosEmpregadoresAtivos();
      const jsonListEmpregador = response.data;
      setAvailableEmpregadores(jsonListEmpregador);
      setEmpregador(jsonListEmpregador[0].id?.toString()!);
      setLoading(false);
    } catch (err) {
      setEmpregador('');
      setLoading(false);
      console.error(err);
    }
  };


  const getValorPagoPorMovimento = (movimentoSelecionado: string, totais = totaisParcelasSelecionadas) => {
    if (movimentoSelecionado === 'SALDO_COM_DESCONTO' || movimentoSelecionado === 'DISTRIBUIR_CONF_SALDO') {
      return totais.saldoDevedor;
    }

    if (movimentoSelecionado === 'VALOR_FINAL_PARCELA' || movimentoSelecionado === 'DISTRIBUIR_CONF_VALOR_FINAL') {
      return totais.valorBruto;
    }

    return 0;
  };

  const getValorPagoDigitado = () => {
    return parseFloat(valorPagoInput.toString().replace(',', '.')) || 0;
  };

  const isMovimentoDistribuicaoValorDigitado = () => {
    return selectFormaPagamento === 'DISTRIBUIR_CONF_SALDO'
      || selectFormaPagamento === 'DISTRIBUIR_CONF_VALOR_FINAL';
  };

  const getTotalEsperadoParaBaixa = () => {
    return getValorPagoPorMovimento(selectFormaPagamento);
  };

  const atualizarValorPagoSelecionado = (selecionadas: string[], movimentoSelecionado = selectFormaPagamento) => {
    const parcelasSelecionadasAtualizadas = (parcelasList ?? []).filter((item) =>
      selecionadas.includes(getParcelaQuitacaoKey(item))
    );
    const totais = calcularTotaisParcelasQuitacao(parcelasSelecionadasAtualizadas);
    const valor = getValorPagoPorMovimento(movimentoSelecionado, totais);

    setValorPago(valor);
    setValorPagoInput(valor);
  };

  const selecionarTodasParcelas = (marcar: boolean) => {
    const selecionadas = marcar && parcelasList
      ? parcelasList.filter((item) => !isParcelaBloqueada(item)).map(getParcelaQuitacaoKey)
      : [];
    setParcelasSelecionadas(selecionadas);
    atualizarValorPagoSelecionado(selecionadas);
  };

  const selecionarParcela = (parcela: OperacaoParcelaFinanceiraEmpregador, marcar: boolean) => {
    if (isParcelaBloqueada(parcela)) return;

    const key = getParcelaQuitacaoKey(parcela);

    setParcelasSelecionadas((selecionadas) => {
      const novasSelecionadas = marcar
        ? (selecionadas.includes(key) ? selecionadas : [...selecionadas, key])
        : selecionadas.filter((item) => item !== key);

      atualizarValorPagoSelecionado(novasSelecionadas);
      return novasSelecionadas;
    });
  };


  const handleEmpregadorChange = (e: any) => {
    setEmpregador(e.target.value);
  };


  const listarParcelas = async () => {
    try {
      setParcelasList([]);
      setLoading(true);
      let dto: BaixaParcelaFinanceiraPorEmpregadorDTO = new BaixaParcelaFinanceiraPorEmpregadorDTO();
      dto.idEmpregador = empregador;
      dto.isFinanceiro = naturezaBaixa;
      dto.vencimento = dtMovimento;
      dto.dtMovimento = parse(dtMovimento, "dd/MM/yyyy", new Date());

      const responseList = await operacaoParcelaService.listarParcelasParaBaixaFinanceirasEmpregador(dto);
      const json = responseList.data;
      const parcelasCalculadas: OperacaoParcelaFinanceiraEmpregador[] = json.parcelas || [];
      setNomeEmpregador(json.parcelas[0].nomeEmpregador);

      const parcelasSelecionadasPadrao = parcelasCalculadas
        .filter((item) => !isParcelaBloqueada(item))
        .map(getParcelaQuitacaoKey);

      const parcelasAtivasIniciais = parcelasCalculadas.filter((item) => !isParcelaBloqueada(item));
      const totaisSelecionados = calcularTotaisParcelasQuitacao(parcelasAtivasIniciais);
      const valorPagoSelecionado = getValorPagoPorMovimento(selectFormaPagamento, totaisSelecionados);

      setBaixaDto(json);
      setParcelasList(parcelasCalculadas);
      setParcelasSelecionadas(parcelasSelecionadasPadrao);
      setNomeCliente(json.nomeCliente);
      setValorPago(valorPagoSelecionado);
      setValorPagoInput(valorPagoSelecionado);

      if (json.bloqueioPropostaEsteira !== null) {
        setAlert({ message: 'Não é possível baixar parcelas deste contrato, proposta - ' + json.bloqueioPropostaEsteira + ' - de REFIN na esteira.', type: 'warning' });
      }

      setExibeDetalhe(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  }

  const executarBaixaParcelas = async (confirmarValorPagoInferiorTotal = false) => {
    if (parse(dtMovimento, "dd/MM/yyyy", new Date()) > new Date()) return setAlert({ message: 'Data de movimento não pode ser superior a data de hoje', type: 'warning' });
    try {
      if (parcelasSelecionadasList.length === 0) return setAlert({ message: 'Selecione ao menos uma parcela para baixa', type: 'warning' });

      setLoading(true);
      let dto: BaixaParcelaDTO = new BaixaParcelaDTO();
      dto.dtMovimento = parse(dtMovimento, "dd/MM/yyyy", new Date());
      dto.valorMovimento = getValorPagoDigitado();
      dto.movimento = selectFormaPagamento;
      dto.parcelas = parcelasSelecionadasList;
      dto.idHistoricoFinanceiro = historicoFinanceiro === '' ? null : parseInt(historicoFinanceiro);
      dto.bloqueioPropostaEsteira = baixaDto?.bloqueioPropostaEsteira!;
      dto.confirmarValorPagoInferiorTotal = confirmarValorPagoInferiorTotal;

      await operacaoParcelaService.baixarParcelasFinanceiras('', dto);
      setLoading(false);
      setAlert({ message: 'Parcelas baixadas com sucesso', type: 'success' });

      limpaDados();
    } catch (err: any) {
      setLoading(false);
      const mensagemErro = err.response?.data || 'Ocorreu um erro ao baixar parcelas';
      if (mensagemErro.includes('O valor pago informado é inferior ao total')) {
        setConfirmarBaixaValorInferior(true);
      } else {
        setAlert({ message: mensagemErro, type: 'error' });
      }
      console.error(err);
    }
  }

  const baixarParcelas = async () => {
    if (parcelasSelecionadasList.length === 0) return setAlert({ message: 'Selecione ao menos uma parcela para baixa Financeira', type: 'warning' });

    if (isMovimentoDistribuicaoValorDigitado()
      && getValorPagoDigitado() + 0.000001 < getTotalEsperadoParaBaixa()) {
      setConfirmarBaixaValorInferior(true);
      return;
    }

    await executarBaixaParcelas(false);
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    setSelectFormaPagamento(selectedValue);
    const valor = getValorPagoPorMovimento(selectedValue);
    setValorPago(valor);
    setValorPagoInput(valor);
  };

  const handleFinanceiraChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    setNaturezaBaixa(selectedValue);
  };

  const handleInputChange = (e: any) => {
    let value = e.target.value;
    value = value.replace(/\D/g, '');
    if (value) {
      value = (parseFloat(value) / 100).toFixed(2);
      value = value.replace('.', ',');
    }
    setValorPagoInput(value);
  };


  const totalValorPagoCalculado = useMemo(() => {
    if (!parcelasList || parcelasList.length === 0) return 0;

    return parcelasList
      .filter((parcela) => parcelasSelecionadas.includes(getParcelaQuitacaoKey(parcela)))
      .reduce((total, parcela) => {
        const valor = parcela.valorParcela || 0;
        return total + valor;
      }, 0);
  }, [parcelasList, parcelasSelecionadas]);

  const limpaDados = () => {
    setExibeDetalhe(false);
    setNomeCliente('');
    setFaixaParcela1('');
    setFaixaParcela2('');
    setValorPago(0);
    setParcelasList([]);
    setParcelasSelecionadas([]);
  }

  useEffect(() => {
    listarEmpregadores();
  }, []);

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}

      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <>
            <div className="card" style={{ marginTop: '25px' }}>
              <div className="card-body">
                <div className="card-header">
                  <strong>Baixa FInanceira de Parcelas</strong>
                </div>

                <div className="position-relative m-md-3">
                  {loading ? (
                    <Spinner loading={loading} />
                  ) : (
                    <div className="card" style={{ marginTop: '25px' }}>
                      <div className="card-body">
                        <div className="row g-3 align-items-end">

                          {/* Campo Empregador */}
                          <div className="col-md-4 col-lg-3">
                            <label className="form-label" htmlFor="Campo-obrigatorio">
                              Empregador<span style={{ color: 'red' }}>*</span>
                            </label>
                            <select
                              className="form-select"
                              id="Campo-obrigatorio"
                              defaultValue={empregador}
                              value={empregador}
                              onChange={(e) => {
                                handleEmpregadorChange(e);
                              }}
                            >
                              {availableEmpregadores.map((item) => (
                                <option key={item.id} value={item.id!}>
                                  {item.id + ' - ' + item.nome}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Campo Natureza Baixa */}
                          <div className="col-md-4 col-lg-3">
                            <label className="form-label">Natureza Baixa</label>
                            <select
                              className="form-select"
                              value={naturezaBaixa}
                              onChange={handleFinanceiraChange}
                            >
                              <option style={{ backgroundColor: 'white' }} value="TODOS">
                                Todos
                              </option>
                              <option style={{ backgroundColor: 'white' }} value="0">
                                Baixa Não Financeira
                              </option>
                              <option style={{ backgroundColor: 'white' }} value="1">
                                Baixa Financeira
                              </option>
                            </select>
                          </div>

                          {/* Campo Competência */}
                          <div className="col-md-4 col-lg-2">
                            <label className="form-label">Competência</label>
                            <DateInput
                              required
                              className="form-control"
                              type="text"
                              placeholder="MM/AAAA"
                              value={dtMovimento}
                              onChange={(e) => {
                                let value = e.target.value.replace(/\D/g, '');

                                if (value.length > 2) {
                                  value = value.substring(0, 2) + '/' + value.substring(2, 6);
                                }

                                setDtMovimento(value);
                              }}
                            />
                          </div>

                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="row" style={{ marginTop: '15px' }}>
                  <div className="col-md-3">
                    <button className="input-group-btn" id="App-button-cor-principal"
                      style={{
                        width: '40%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '50px', minWidth: '50px'
                      }} onClick={listarParcelas}>
                      <a>
                        <FontAwesomeIcon icon={faCalculator} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                      </a>
                      <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Calcular</strong>
                    </button>
                  </div>
                </div>

                {exibeDetalhe ? (
                  <>
                    <div className="position-relative m-md-3">
                      {loading ? (
                        <Spinner loading={loading} />
                      ) : (
                        <div className="card" style={{ marginTop: '25px' }}>
                          <div className="card-body">

                            {/* Resumo do DTO em 1 Linha */}
                            <div className="row">
                              <div className="col-md-3" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                <label style={{ width: '100%', fontWeight: 'bold' }}>Empregador</label>
                              </div>
                              <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                <label style={{ width: '100%', fontWeight: 'bold' }}>Quantidade de parcelas</label>
                              </div>
                              <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                <label style={{ width: '100%', fontWeight: 'bold' }}>Total de parcelas</label>
                              </div>
                              <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                <label style={{ width: '100%', fontWeight: 'bold' }}>Total financeiro</label>
                              </div>
                              <div className="col-md-3" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                <label style={{ width: '100%', fontWeight: 'bold' }}>Total não financeiro</label>
                              </div>
                            </div>

                            <div className="row" style={{ marginTop: '5px' }}>
                              <div className="col-md-3" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                {baixaDto?.idEmpregador + ' - ' + nomeEmpregador}
                              </div>
                              <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                {baixaDto?.parcelas?.length || 0}
                              </div>
                              <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                {'R$ ' + formatarValorMoeda(baixaDto?.totalParcelas)}
                              </div>
                              <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                {'R$ ' + formatarValorMoeda(baixaDto?.totalFinanceiro)}
                              </div>
                              <div className="col-md-3" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                {'R$ ' + formatarValorMoeda(baixaDto?.totalNaoFinanceiro)}
                              </div>
                            </div>

                          </div>
                        </div>
                      )}
                    </div>

                    {parcelasList != null && parcelasList!.length > 0 ? (
                      <div className="table-responsive">
                        <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                          <table cellSpacing="2" cellPadding="4" rules="all" style={{ border: '3', color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }} className="table table-bordered table-striped">
                            <tbody>
                              <tr className="GridHeaderStyle" style={{ backgroundColor: 'white', color: 'black' }}>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">
                                  <input
                                    type="checkbox"
                                    checked={todasParcelasSelecionadas}
                                    onChange={(e) => selecionarTodasParcelas(e.target.checked)}
                                    aria-label="Selecionar todas as parcelas"
                                    disabled={parcelasHabilitadas.length === 0}
                                  /> Tudo
                                </th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">NÚMERO CONTRATO</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">NÚMERO</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">VALOR PARCELA</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">NATUREZA BAIXA</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">VENCIMENTO</th>
                              </tr>
                              {parcelasList.map((item) => {
                                const bloqueada = isParcelaBloqueada(item);
                                return (
                                  <tr className="GridRowStyle" key={getParcelaQuitacaoKey(item)} style={{ backgroundColor: bloqueada ? 'lightgray' : 'white', color: bloqueada ? 'black' : 'black' }}>
                                    <td style={{ textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={parcelasSelecionadas.includes(getParcelaQuitacaoKey(item))}
                                        disabled={bloqueada}
                                        onChange={(e) => selecionarParcela(item, e.target.checked)}
                                        aria-label={`Selecionar parcela ${item.numeroParcela || ''}`}
                                      />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{item.numeroContrato}</td>
                                    <td style={{ textAlign: 'center' }}>{item.numeroParcela}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      R$ {formatarValorMoeda(item.valorParcela)}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{item.isFinanceira ? 'BAIXA FINANCEIRA' : 'BAIXA NAO FINANCEIRA'}</td>
                                    <td style={{ textAlign: 'center' }}>{item.dtVencimento ? format(parseISO(item.dtVencimento.toString()), 'dd/MM/yyyy') : ''}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Campo Valor Final com Totalizador das Parcelas Selecionadas */}
                        <div className="row my-3">
                          <div className="col-md-4">
                            <div className="card bg-light border-primary">
                              <div className="card-body p-2 d-flex justify-content-between align-items-center">
                                <strong>Soma das Parcelas Selecionadas:</strong>
                                <span className="fs-5 text-primary fw-bold">
                                  R$ {formatarValorMoeda(totalValorPagoCalculado)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {baixaDto?.bloqueioPropostaEsteira === null && (
                          <div className="row" style={{ marginTop: '10px', height: '50px', maxWidth: '70%' }}>
                            <div className="col-md-3">
                              <button className="input-group-btn" id="App-button-cor-principal"
                                style={{
                                  width: '80%', display: 'flex', alignItems: 'center',
                                  justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                                }} onClick={baixarParcelas}>
                                <a>
                                  <FontAwesomeIcon icon={faSave} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                                </a>
                                <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Confirmar Baixa</strong>
                              </button>
                            </div>
                            <div className="col-md-3">
                              <button className="input-group-btn"
                                style={{
                                  width: '80%', display: 'flex', alignItems: 'center', backgroundColor: 'red', borderColor: 'red',
                                  justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                                }} onClick={() => window.history.back()}>
                                <a>
                                  <FontAwesomeIcon icon={faClose} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                                </a>
                                <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Cancelar</strong>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : parcelasList != null ? (
                      <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                        <span style={{ color: 'red' }}>Não existem parcelas para o filtro informado!</span>
                      </div>
                    ) : null}
                  </>
                ) : null}

              </div>
            </div>
          </>
        )}
      </div >
    </>
  );
}

export default BaixaParcelaFinanceiraPorEmpregadorComponent;